import { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { getValue } from 'firebase/remote-config';
import { remoteConfig } from './lib/firebase';
import { useGames } from './hooks/useGames';
import type { Game } from './hooks/useGames';
import { useGamesLive } from './hooks/useGamesLive';
import { useUser, UserProvider } from './contexts/UserContext';
import { useToast, ToastContainer } from './components/ToastNotification';
import { postMessageHandler, POST_MESSAGE_ACTIONS } from './utils/postMessageHandler';
import { replaceUrlParameters } from './lib/config';
import log from './utils/logger';

// Custom hooks
import { useFirebaseAuth } from './hooks/useFirebaseAuth';
import { useRemoteConfig } from './hooks/useRemoteConfig';
import { useUrlParameters } from './hooks/useUrlParameters';
import { useGameLaunch } from './hooks/useGameLaunch';

// Components
import FullscreenLoader from './components/FullscreenLoader';
import AuthForm from './components/AuthForm';
import SearchTrigger from './components/SearchTrigger';
import SearchPage from './components/SearchPage';
import GameModal from './components/GameModal';
import FullscreenIframe from './components/FullscreenIframe';
import ErrorBoundary from './components/ErrorBoundary';
import TabBar, { tabs, TabId } from './components/TabBar';
import Header from './components/Header';
import NavigationMenu from './components/NavigationMenu';
import MainContent from './components/MainContent';
import FilterPage from './components/FilterPage';
import PromoBanner from './components/PromoBanner';

const REQUIRE_AUTH = import.meta.env.VITE_REQUIRE_AUTH === 'true';

function CasinoApp() {
  // State
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [isGameModalOpen, setIsGameModalOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isPromoOpen, setIsPromoOpen] = useState(false);
  const [gamePromoUrl, setGamePromoUrl] = useState<string | null>(null);
  const [isGamePromoOpen, setIsGamePromoOpen] = useState(false);
  const [isWebViewOpen, setIsWebViewOpen] = useState(false);
  const [webViewUrl, setWebViewUrl] = useState<string | null>(null);
  const [isBannerOpen, setIsBannerOpen] = useState(false);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<TabId>(tabs[0].id);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [filteredGames, setFilteredGames] = useState<Game[] | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({});
  const hasAutoLaunchedRef = useRef(false);
  const notificationShown = useRef(false);
  const initializedRef = useRef(false);

  // Custom hooks
  const { user, loading: authLoading, setUser } = useFirebaseAuth(REQUIRE_AUTH);
  const { loaded: remoteConfigLoaded, error: remoteConfigError } = useRemoteConfig();
  const urlParams = useUrlParameters();
  const { gamesLive } = useGamesLive(selectedTab === TabId.CASINO_LIVE);
  const { user: gameUser, setUser: setGameUser, createUserFromParams, updateUserToken, logout, refreshBalance } = useUser();
  const { toasts, removeToast, showError, showWarning } = useToast();
  
  // Data fetching
  const {
    data,
    loading,
    error,
    selectedMenuId,
    loadData,
    setSelectedMenuId,
    clearError,
    getAllGames
  } = useGames();

  // Helper functions
  const getCurrentGameType = useCallback(() => {
    const currentTab = tabs.find(tab => tab.id === selectedTab);
    return currentTab?.gameType || tabs[0].gameType || 'slot';
  }, [selectedTab]);

  const getSelectedFiltersCount = () => {
    return Object.values(appliedFilters).flat().length;
  };

  const findLiveGameById = useCallback((gameId: string) => {
    return gamesLive.find(game => game.id === gameId);
  }, [gamesLive]);

  // Game launch hook
  const {
    isGameLoading,
    isGameIframeOpen,
    gameUrl,
    currentGameName,
    lobbyUrl,
    handleGamePlay,
    handleGameIframeClose,
    createLoginParamsString
  } = useGameLaunch(
    gameUser,
    updateUserToken,
    logout,
    selectedMenuId,
    getCurrentGameType,
    findLiveGameById,
    showError,
    showWarning
  );

  // Update selected tab from URL params
  useEffect(() => {
    if (urlParams.tabParam && tabs.some(tab => tab.id === urlParams.tabParam)) {
      setSelectedTab(urlParams.tabParam as TabId);
    }
  }, [urlParams.tabParam]);

  // Debug log for URL parameters
  useEffect(() => {
    log.info('URL Parameters:', {
      userParam: urlParams.userParam,
      cartaParam: urlParams.cartaParam,
      tokenParam: urlParams.tokenParam,
      tabParam: urlParams.tabParam,
      menuParam: urlParams.menuParam,
      launchCodeParam: urlParams.launchCodeParam,
      partnerIdParam: urlParams.partnerIdParam,
      platformIdParam: urlParams.platformIdParam,
      webViewParam: urlParams.webViewParam
    });
  }, [urlParams.userParam, urlParams.cartaParam, urlParams.tokenParam, urlParams.tabParam, urlParams.menuParam, urlParams.launchCodeParam, urlParams.partnerIdParam, urlParams.platformIdParam, urlParams.webViewParam]);

  // Create game user from URL parameters
  useEffect(() => {
    if (urlParams.userParam && urlParams.cartaParam && urlParams.tokenParam) {
      if (!gameUser) {
        const newGameUser = createUserFromParams(urlParams.userParam, urlParams.cartaParam, urlParams.tokenParam);
        setGameUser(newGameUser);
        log.debug('🎮 Utente del gioco creato:', newGameUser);
      }
    } else {
      if (gameUser) {
        setGameUser(null);
        log.debug('🎮 Utente del gioco rimosso - parametri URL mancanti');
      }
    }
  }, [urlParams.userParam, urlParams.cartaParam, urlParams.tokenParam, gameUser, createUserFromParams, setGameUser]);

  // Handle webView parameter
  useEffect(() => {
    if (!remoteConfigLoaded) return;
    
    if (urlParams.webViewParam) {
      log.debug('🌐 WebView parameter detected:', urlParams.webViewParam);
      
      const promoQueryParamsValue = getValue(remoteConfig, 'promoQueryParams');
      const promoQueryParams = promoQueryParamsValue.asString();
      
      let finalUrl = urlParams.webViewParam;
      
      if (promoQueryParams) {
        const cleanedParams = promoQueryParams.replace(/^[?&]+/, '');
        
        if (cleanedParams) {
          const separator = urlParams.webViewParam.includes('?') ? '&' : '?';
          finalUrl = urlParams.webViewParam + separator + cleanedParams;
          log.debug('🔗 Appending promo params to webView URL:', finalUrl);
        }
      }
      
      setWebViewUrl(finalUrl);
      setIsWebViewOpen(true);
    }
  }, [urlParams.webViewParam, remoteConfigLoaded]);

  // Authentication and initial data loading
  useEffect(() => {
    if (!remoteConfigLoaded) return;
    
    const currentGameType = getCurrentGameType();
    const menu = urlParams.menuParam ?? null;

    if (REQUIRE_AUTH) {
      if (user && !notificationShown.current) {
        loadData(menu, currentGameType, false, urlParams.isStaticData).then(() => setIsLoaded(true));
        notificationShown.current = true;
      }
    } else {
      if (!initializedRef.current) {
        loadData(menu, currentGameType, false, urlParams.isStaticData).then(() => setIsLoaded(true));
        initializedRef.current = true;
      }
    }
  }, [remoteConfigLoaded, user, loadData, urlParams.menuParam, urlParams.isStaticData, getCurrentGameType]);

  // Auto-launch game from URL parameters
  useEffect(() => {
    const hasLaunchParams = urlParams.launchCodeParam && urlParams.platformIdParam;
    
    if (hasLaunchParams && !hasAutoLaunchedRef.current && isLoaded && !isGameLoading && !isGameIframeOpen) {
      if (gameUser && gameUser.isLogged) {
        hasAutoLaunchedRef.current = true;
        
        const gameFromUrl: Game = {
          id: 'url-game',
          name: urlParams.gameNameParam || 'Game from URL',
          gameCode: 'url-game',
          launchCode: urlParams.launchCodeParam!,
          type: getCurrentGameType(),
          subtype: '',
          platform: {
            id: parseInt(urlParams.platformIdParam!, 10),
            color: '#000000'
          },
          provider: '',
          external: true,
          order: 0,
          info: {
            rtp: undefined,
            branded: undefined,
            lines: undefined,
            volatility: undefined,
            reels: undefined,
            jackpot: undefined,
            jackpotValue: undefined,
            functionality: undefined,
            theme: undefined,
            exclusive: undefined
          },
          images: {
            thumbURL: undefined,
            landscapeImgURL: undefined,
            otherImgURL: undefined
          },
          partnerID: urlParams.partnerIdParam || '',
          isNew: false,
          docID: 'url-game'
        };
        
        log.debug('🚀 Auto-launching game from URL parameters:', gameFromUrl);
        handleGamePlay(gameFromUrl);
      }
    }
  }, [urlParams.launchCodeParam, urlParams.platformIdParam, urlParams.partnerIdParam, gameUser, isLoaded, isGameLoading, isGameIframeOpen, handleGamePlay, getCurrentGameType]);

  const handleTabSelect = async (tab: { id: TabId; gameType?: string; label?: string; icon?: string }) => {
    if (tab.id === TabId.FAVORITES) {
      return;
    }
    
    if (tab.gameType && tab.id !== selectedTab) {
      setSelectedTab(tab.id);
      try {
        const gameType = tab.gameType;
        const result = await loadData(null, gameType, true, urlParams.isStaticData);
        if (result && result.menu && result.menu.length > 0) {
          setSelectedMenuId(result.menu[0].id);
        }
        setIsLoaded(true);
      } catch {
        showError('Errore caricamento', 'Impossibile caricare i giochi del tab selezionato');
      }
    }
  };

  const handleMenuSelect = async (menuId: string) => {
    if (menuId !== selectedMenuId) {
      setSelectedMenuId(menuId);
      setFilteredGames(null); // Reset filtered games when changing menu
      setAppliedFilters({}); // Clear applied filters when changing menu
      try {
        await loadData(menuId, getCurrentGameType(), false, urlParams.isStaticData);
      } catch {
        showError('Errore caricamento', 'Impossibile caricare i giochi del menu selezionato');
      }
    }
  };

  const handleGameInfo = useCallback((game: Game) => {
    setSelectedGame(game);
    setIsGameModalOpen(true);
  }, []);

  const handleGameSearch = (game: Game) => {
    handleGameInfo(game);
  };

  const handleRetry = () => {
    clearError();
    loadData(selectedMenuId, getCurrentGameType(), false, urlParams.isStaticData);
  };

  const handlePromoClick = () => {
    const promoUrlValue = getValue(remoteConfig, 'promoURL');
    const promoUrl = promoUrlValue.asString();
    
    if (promoUrl) {
      log.debug('Opening promo URL:', promoUrl);
      setIsPromoOpen(true);
    }
  };

  const handlePromoClose = () => {
    setIsPromoOpen(false);
  };

  const handleGamePromoClick = useCallback((promoUrl: string) => {
    setGamePromoUrl(promoUrl);
    setIsGamePromoOpen(true);
  }, []);

  const handleGamePromoClose = () => {
    setIsGamePromoOpen(false);
    setGamePromoUrl(null);
  };

  const handleWebViewClose = () => {
    setIsWebViewOpen(false);
    setWebViewUrl(null);
    urlParams.clearWebViewParam();
  };

  const handleBonusClick = useCallback((bonusUrl: string) => {
    if (gameUser && gameUser.isLogged) {
      const processedUrl = replaceUrlParameters(bonusUrl, gameUser);
      log.debug('💰 Opening bonus URL:', processedUrl);
      setWebViewUrl(processedUrl);
      setIsWebViewOpen(true);
    } else {
      log.debug("🎯 User not logged, sending login request with bonus URL");
      const paramsString = createLoginParamsString({
        webView: bonusUrl
      });
      log.debug("🎯 Sending postMessage with params:", paramsString);
      postMessageHandler.sendToParent(POST_MESSAGE_ACTIONS.LOGIN_FOR_CASINO, { params: paramsString });
    }
  }, [gameUser, createLoginParamsString]);

  const handleBannerClick = useCallback((url: string) => {
    log.debug('🎯 Banner clicked, opening URL:', url);
    setBannerUrl(url);
    setIsBannerOpen(true);
  }, []);

  const handleBannerClose = () => {
    setIsBannerOpen(false);
    setBannerUrl(null);
  };

  // Handler for login messages from iframe
  const handleLoginMessage = useCallback((event: MessageEvent) => {
    try {
      const messageData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      
      if (messageData.action === POST_MESSAGE_ACTIONS.LOGIN && messageData.backUrl) {
        log.debug('📨 Login message received from iframe:', messageData);
        
        const paramsString = createLoginParamsString({
          webView: messageData.backUrl
        });
        
        postMessageHandler.sendToParent(POST_MESSAGE_ACTIONS.LOGIN_FOR_CASINO, { params: paramsString });
        
        if (isPromoOpen) {
          setIsPromoOpen(false);
        }
      }
    } catch (error) {
      log.error('Error parsing login message:', error);
    }
  }, [createLoginParamsString, isPromoOpen]);

  // Register handler for login messages from iframe
  useEffect(() => {
    postMessageHandler.on(POST_MESSAGE_ACTIONS.LOGIN, handleLoginMessage);
    
    return () => {
      postMessageHandler.off(POST_MESSAGE_ACTIONS.LOGIN);
    };
  }, [handleLoginMessage]);

  // Show loader while Remote Config is loading
  if (!remoteConfigLoaded && !remoteConfigError) {
    return (
      <div className="min-h-screen snai-bg-primary flex items-center justify-center">
        <FullscreenLoader 
          isVisible={true} 
          showBackground={false}
          size="large"
        />
      </div>
    );
  }

  // Show error if Remote Config failed to load
  if (remoteConfigError) {
    return (
      <div className="min-h-screen snai-bg-primary flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-xl mb-4">{remoteConfigError}</div>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded transition-colors"
          >
            Ricarica pagina
          </button>
        </div>
      </div>
    );
  }

  if (authLoading) {
    return (
      <div className="min-h-screen snai-bg-primary flex items-center justify-center">
        <FullscreenLoader 
          isVisible={true} 
          showBackground={false}
          size="large"
        />
      </div>
    );
  }

  if (REQUIRE_AUTH && !user) {
    return (
      <div className="min-h-screen bg-snai-primary flex items-center justify-center">
        <div className="p-6">
          <AuthForm user={user} onAuthChange={setUser} />
        </div>
      </div>
    );
  }

  if (!isLoaded && loading) {
    return (
      <div className="min-h-screen bg-snai-primary flex items-center justify-center">
        <FullscreenLoader
          isVisible={true}
          showBackground={false}
          size="large"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-snai-primary flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-xl mb-4">{error}</div>
          <div className="space-y-3">
            <button
              onClick={handleRetry}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded transition-colors"
            >
              Riprova
            </button>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
            >
              Ricarica pagina
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen snai-bg-primary flex flex-col overflow-hidden">
      {/* Fixed header section - outside scrollable area */}
      <div className="flex-shrink-0 z-50 snai-bg-primary">
        <Header 
          onPromoClick={handlePromoClick}
          onBonusClick={handleBonusClick}
        />
        <div className="px-6 py-2">
          <div className="">
            <SearchTrigger onSearchOpen={() => setIsSearchOpen(true)} />
          </div>
        </div>
      </div>

      {/* Main content container with scroll */}
      <div className="flex-1 flex flex-col overflow-hidden pb-16 pb-safe">
        {/* Scrollable content */}
        <div className="flex-1 flex flex-col overflow-y-auto">
          {/* Promo Banner - only show for categories style */}
          {(() => {
            const selectedMenu = data?.menu?.find(menu => menu.id === selectedMenuId);
            return selectedMenu?.style === "categories" && <PromoBanner selectedTab={selectedTab} onBannerClick={handleBannerClick} />;
          })()}
          
          {/* Navigation Menu - sticky when scrolling */}
          <div className="sticky top-0 z-40 snai-bg-primary">
            {data?.menu && (
              <NavigationMenu
                menus={data.menu}
                selectedMenuId={selectedMenuId}
                loading={loading}
                onMenuSelect={handleMenuSelect}
                filters={data.filters}
                onFiltersClick={() => setIsFiltersOpen(true)}
                selectedFiltersCount={getSelectedFiltersCount()}
              />
            )}
          </div>

          <MainContent
            data={data}
            selectedMenuId={selectedMenuId}
            selectedTab={selectedTab}
            gamesLive={gamesLive}
            loading={loading}
            onGamePlay={handleGamePlay}
            onGameInfo={handleGameInfo}
            onGamePromoClick={handleGamePromoClick}
            filteredGames={filteredGames}
          />
          {/* Spacer for TabBar */}
          <div className="h-24"></div>
        </div>
      </div>

      <GameModal
        game={selectedGame}
        isOpen={isGameModalOpen}
        onClose={() => setIsGameModalOpen(false)}
        onPlay={handleGamePlay}
      />

      <FullscreenIframe
        gameUrl={gameUrl}
        isOpen={isGameIframeOpen}
        onClose={() => {
          handleGameIframeClose();
          urlParams.clearGameParams();

          // Refresh balance after 5 seconds
          setTimeout(() => {
            refreshBalance();
          }, 5000);
        }}
        gameName={currentGameName}
        lobbyUrl={lobbyUrl ?? undefined}
      />

      <ToastContainer
        toasts={toasts}
        onRemove={removeToast}
      />

      <SearchPage
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onGameSelect={handleGameSearch}
        onGamePlay={handleGamePlay}
        user={user}
        suggestions={data?.suggestions}
      />

      {isFiltersOpen && data?.filters && (
        <FilterPage
          filters={data.filters}
          games={getAllGames()}
          onClose={() => setIsFiltersOpen(false)}
          onApplyFilters={(games, filters) => {
            setFilteredGames(games);
            setAppliedFilters(filters);
          }}
          initialSelectedFilters={appliedFilters}
        />
      )}

      <TabBar
        selectedTab={selectedTab}
        onTabSelect={handleTabSelect}
      />

      <FullscreenLoader 
        isVisible={isGameLoading}
      />

      {/* Promo iframe with higher z-index to cover everything */}
      {isPromoOpen && (
        <div className="fixed inset-0 z-[100]">
          <FullscreenIframe
            gameUrl={getValue(remoteConfig, 'promoURL').asString()}
            isOpen={isPromoOpen}
            onClose={handlePromoClose}
            gameName="Promo"
          />
        </div>
      )}

      {/* WebView iframe with higher z-index to cover everything */}
      {isWebViewOpen && webViewUrl && (
        <div className="fixed inset-0 z-[100]">
          <FullscreenIframe
            gameUrl={webViewUrl}
            isOpen={isWebViewOpen}
            onClose={handleWebViewClose}
            gameName="WebView"
          />
        </div>
      )}

      {/* Game Promo iframe with higher z-index to cover everything */}
      {isGamePromoOpen && gamePromoUrl && (
        <div className="fixed inset-0 z-[100]">
          <FullscreenIframe
            gameUrl={gamePromoUrl}
            isOpen={isGamePromoOpen}
            onClose={handleGamePromoClose}
            gameName="Game Promo"
          />
        </div>
      )}

      {/* Banner iframe with higher z-index to cover everything */}
      {isBannerOpen && bannerUrl && (
        <div className="fixed inset-0 z-[100]">
          <FullscreenIframe
            gameUrl={bannerUrl}
            isOpen={isBannerOpen}
            onClose={handleBannerClose}
            gameName="Banner"
          />
        </div>
      )}

    </div>
  );
}

function AppWithProvider() {
  const urlParams = useUrlParameters();
  
  return (
    <UserProvider onLogout={urlParams.clearAllParams}>
      <CasinoApp />
    </UserProvider>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<div className="min-h-screen bg-snai-primary flex items-center justify-center"><FullscreenLoader isVisible={true} showBackground={false} size="large" /></div>}>
        <AppWithProvider />
      </Suspense>
    </ErrorBoundary>
  );
}